import { z } from 'zod';
/**
 * The FR-05 grading vocabulary — the words and numbers the planogram score is
 * expressed in.
 *
 * ITS OWN MODULE, on the reasoning `settings.js` gives for itself. Five surfaces
 * read this vocabulary — the manager console, the staff app, the planogram
 * exporter, the visit exporter and the settings form — and only one of them owns
 * any of the data. `vision.js` imports the enums from here rather than the other
 * way round, because the scorer is one consumer of the vocabulary and not its
 * home.
 *
 * WHAT IS FROZEN HERE AND WHAT IS NOT. The grades, the ranks and their labels
 * are TERMS OF ART the product owner defined, so they live in one place for the
 * reason `COMPETITION_GRADE_LABELS` does: an export whose wording drifts from
 * the screen it was exported from is a support conversation about whether the
 * two disagree. The POINTS and WEIGHTS are frozen for a narrower reason — three
 * surfaces have to PRINT the arithmetic, and three hand-copied weight vectors is
 * three chances to publish 0.25 where the formula says 0.20. Nothing on a client
 * may SCORE with them; see the note on the weights below.
 */
/* -------------------------------------------------------------------------- */
/* The four metrics                                                           */
/* -------------------------------------------------------------------------- */
/**
 * The four things FR-05 measures about a shelf.
 *
 * AVAILABILITY IS NOT LIKE THE OTHER THREE, and every surface that renders this
 * enum has to know it. Facing, sequence and row are properties of one SKU on one
 * photo. Availability is a property of the PHOTO — how much of the range is on
 * the shelf at all — computed once and broadcast identically onto every SKU row
 * belonging to that photo. Aggregating it at SKU grain therefore measures which
 * photographs a SKU happened to appear on and nothing about the SKU itself.
 */
export const PlanogramMetric = z.enum(['AVAILABILITY', 'FACING', 'SEQUENCE', 'ROW']);
/**
 * The metric names as the product owner says them, in Indonesian.
 *
 * "Facing" is NOT translated, on the precedent `IMPORT_ENTITY_LABELS` sets for
 * "SKU": it is the word the business uses out loud, it is already the header on
 * the planogram report and the stepper label in the staff app, and it is the
 * accepted alias on the master-data import.
 */
export const PLANOGRAM_METRIC_LABELS = Object.freeze({
    AVAILABILITY: 'Ketersediaan',
    FACING: 'Facing',
    SEQUENCE: 'Urutan',
    ROW: 'Baris',
});
/* -------------------------------------------------------------------------- */
/* Grades and ranks                                                           */
/* -------------------------------------------------------------------------- */
/**
 * How one SKU scored on one metric.
 *
 * Declared BEST FIRST, and the order is load-bearing twice: `enums.ts` derives
 * the PostgreSQL type from `.options`, so `ORDER BY facing_grade` sorts A before
 * C without a CASE, and the settings legend renders the bands in this order.
 *
 * THERE IS NO LABEL MAP FOR THIS ENUM, deliberately. The letter is the
 * vocabulary — freezing `{ A: 'A' }` would be ceremony — and any gloss worth
 * printing is metric-specific ("too few distinct SKUs" for availability, "beyond
 * the row tolerance" for row), which is not a shape a `Record<PlanogramGrade,
 * string>` can hold. The glosses that are needed are worked examples computed
 * from the administrator's own draft on the settings page.
 */
export const PlanogramGrade = z.enum(['A', 'B', 'C']);
/**
 * The overall standing of one photo, from its total score.
 *
 * Best first, for the `ORDER BY` reason above. There is no member for "not
 * ranked": a photo scored before letter grading existed carries a null rank
 * beside its score, which is the same statement `CompetitionGrade` makes by
 * refusing a fourth member for "no data". `PLANOGRAM_NO_RANK_LABEL` is what the
 * surfaces print in that case.
 */
export const PlanogramRank = z.enum(['DIAMOND', 'GOLD', 'SILVER', 'BRONZE', 'COAL']);
/**
 * The ranks, in the product owner's own words.
 *
 * THE METAL NAMES STAY IN ENGLISH. They are what the client wrote, they are the
 * international convention in field-sales tooling, and "Batu bara" printed
 * against a named salesperson on a performance report reads as an insult in a
 * way "Coal" does not.
 *
 * A near-identity map still earns its place. The console `Badge` upper-cases
 * through CSS and PDFKit does not, so without one frozen source the screen says
 * `Diamond`, the printed page says `DIAMOND`, and the workbook says whichever
 * the exporter's author typed. It is also the one-line edit if the client
 * changes their mind.
 */
export const PLANOGRAM_RANK_LABELS = Object.freeze({
    DIAMOND: 'Diamond',
    GOLD: 'Gold',
    SILVER: 'Silver',
    BRONZE: 'Bronze',
    COAL: 'Coal',
});
/**
 * What stands where a rank would go on a photo scored before letter grading.
 *
 * NOT A SIXTH MEMBER OF THE ENUM — see the note on `PlanogramRank` — but frozen
 * here rather than restated per surface, because the console chip, the XLSX cell
 * and the PDF cell must read identically. A reader comparing a workbook against
 * the screen it came from is exactly who this protects.
 */
export const PLANOGRAM_NO_RANK_LABEL = 'Rumus lama';
/* -------------------------------------------------------------------------- */
/* The arithmetic, for printing and never for scoring                         */
/* -------------------------------------------------------------------------- */
/**
 * What each grade is worth.
 *
 * NOTE THE FLOOR. The lowest grade is 50, not 0, so a SKU present on a shelf and
 * wrong on every measurement scores 50 and a photo of such SKUs totals 50. The
 * reachable range of a scored photo is 50–100 and the five ranks partition it
 * evenly at ten points each. A reader arriving with a 0–100 prior will read 50
 * as half marks when it means every measurement failed, which is why no surface
 * may print a graded score without either its rank or the scale beside it.
 *
 * Zero remains reachable by exactly one route: a shelf the salesperson confirmed
 * is empty has no SKU rows to average, and scores 0.
 */
export const PLANOGRAM_GRADE_POINTS = Object.freeze({
    A: 100,
    B: 75,
    C: 50,
});
/**
 * How the four metrics combine into one SKU's score.
 *
 * EXPORTED SO SURFACES CAN STATE THE FORMULA, NOT SO THEY CAN APPLY IT. The
 * settings legend and both export footnotes have to tell a reader what produced
 * the number; that is the whole reason these are not module constants inside the
 * scorer. A client that multiplies by them is re-deriving a verdict the server
 * already sent, which is the mistake `competitionGradeTone` warns about at
 * length — render what the server sent.
 *
 * `planogram.test.ts` asserts these sum to exactly 1.
 */
export const PLANOGRAM_METRIC_WEIGHTS = Object.freeze({
    FACING: 0.3,
    SEQUENCE: 0.2,
    AVAILABILITY: 0.2,
    ROW: 0.3,
});
//# sourceMappingURL=planogram.js.map